import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew'
import { toastStore } from 'shared/ui'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseCustomDomain, ShortLinkApi } from 'entities/ShortLink'
import { UrlShortener } from 'entities/ShortLink/model/UrlShortener'
import { UrlShortenerModalStore } from 'pages/settings/pages/urlShortener/store/urlShortenerModalStore'

export class UrlShortenerStore {
  private readonly _tableStore: TableStore<UrlShortener>
  private readonly _urlShortenerModalStore: UrlShortenerModalStore
  private _initialLoading = true
  private _listMap: Map<number, UrlShortener> = new Map()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._urlShortenerModalStore = new UrlShortenerModalStore(this._entities, this.getCustomDomain)
    this._tableStore = new TableStore({
      key: 'url_shortener',
    })
  }

  init = async () => {
    try {
      await this.getCustomDomain()
    } finally {
      runInAction(() => {
        this._initialLoading = false
      })
    }
  }

  getCustomDomain = async () => {
    try {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      const { data } = await ShortLinkApi.getCustomDomain()

      if (data && !Array.isArray(data)) {
        this.setData(data)
      } else {
        this._listMap.clear()
      }

      return data
    } catch (e) {
      logger.error(e)

      toastStore.add({
        type: 'error',
        title: 'Something went wrong, try again.',
      })

      return []
    } finally {
      runInAction(() => {
        this._initialLoading = false
        this._tableStore.setLoading(false)
      })
    }
  }

  setData = (data: IResponseCustomDomain) => {
    this._listMap.clear()
    const rows = [new UrlShortener(data)]

    rows.forEach((row) => {
      this._listMap.set(row.id, row)
    })

    this._tableStore.setRows(rows)
  }

  get items() {
    return Array.from(this._listMap.values())
  }

  get initialLoading() {
    return this._initialLoading
  }

  get isEmpty() {
    return !this.items.length
  }

  get tableStore() {
    return this._tableStore
  }

  get status() {
    return this.items[0]?.status || ''
  }

  get isStatusDraft() {
    return !!this.status && (this.status === 'draft' || this.status === 'pending')
  }

  get isStatusPending() {
    return (
      !!this.status &&
      (this.status === 'certificate_in_progress' || this.status === 'certificate_processed')
    )
  }

  get isStatusVerificationFailed() {
    return !!this.status && this.status === 'verify_failed'
  }

  get isStatusPendingPayment() {
    return !!this.status && this.status === 'waiting_for_payment'
  }

  get isStatusUnsubscribed() {
    return !!this.status && (this.status === 'unsubscribed' || this.status === 'payment_failed')
  }

  get isStatusActive() {
    return !!this.status && (this.status === 'payed' || this.status === 'active')
  }

  get urlShortenerModalStore() {
    return this._urlShortenerModalStore
  }
}
